import { useRef, useState, type FormEvent } from "react";
import { SectionHeading, SectionShell } from "./common";

const FORM_URL = "https://readdy.ai/api/form/daelsrbep053ijub1dq0";

type Status = "idle" | "sending" | "success" | "error";

const topicOptions = ["线索核实申请", "内容纠错", "转载授权", "广告与商务", "其他"];

export default function ContactSection() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const honeypot = String(fd.get("contact_alt") || "").trim();

    // 蜜罐命中：不向表单服务提交任何内容，仅给出与正常提交一致的通用反馈
    if (honeypot) {
      setStatus("success");
      setFeedback("已收到你的反馈，我们会尽快处理，感谢你的支持。");
      form.reset();
      return;
    }
    fd.delete("contact_alt");

    setStatus("sending");
    setFeedback("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(responseText);
      if (res.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFeedback("反馈已成功提交，我们会尽快核实并回复，感谢你的支持。");
        form.reset();
      } else {
        setStatus("error");
        setFeedback(isSpam || !serverMsg ? "提交未成功，请检查内容后重试。" : serverMsg);
      }
    } catch {
      setStatus("error");
      setFeedback("网络异常，请稍后再试。");
    }
  }

  return (
    <SectionShell id="contact" className="bg-background-100/70 py-14 md:py-20">
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_1.35fr] lg:gap-16">
        <div className="flex flex-col">
          <SectionHeading
            eyebrow="CONTACT"
            title="意见与反馈"
            desc="发现事实错误、想提交可溯源的线索，或对某条议题有不同看法？欢迎告诉我们，我们会在核实后回应或更正。"
          />
          <div className="mt-8 space-y-4">
            <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className="ri-mail-send-line text-lg" />
              </span>
              <div>
                <p className="text-sm font-bold text-foreground-950">内容与合作邮箱</p>
                <p className="mt-1 text-sm text-foreground-600">contact@99heihai.com</p>
              </div>
            </div>
            <div className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent-500 text-background-50">
                <i className="ri-timer-flash-line text-lg" />
              </span>
              <div>
                <p className="text-sm font-bold text-foreground-950">处理承诺</p>
                <p className="mt-1 text-sm text-foreground-600">工作日 48 小时内初读，涉及事实纠错会优先处理并公开更新。</p>
              </div>
            </div>
            <p className="rounded-md bg-background-50 px-4 py-3 text-xs leading-relaxed text-foreground-500">
              提示：为保证信息真实，我们仅跟进可提供信源链接的线索。未经证实的匿名指控不会被采纳或发布。
            </p>
          </div>
        </div>

        <form
          ref={formRef}
          onSubmit={handleSubmit}
          data-readdy-form
          className="feedback-form rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
        >
          <h3 className="font-heading text-xl font-bold text-foreground-950">填写你的反馈</h3>
          <p className="mt-2 text-sm text-foreground-600">
            <i className="ri-lock-line mr-1" />标注 * 为必填，我们仅将信息用于处理你的反馈。
          </p>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground-800">你的称呼 *</span>
              <input
                type="text"
                name="name"
                required
                placeholder="怎么称呼你"
                className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </label>
            <label className="flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground-800">联系邮箱 *</span>
              <input
                type="email"
                name="email"
                required
                placeholder="name@example.com"
                className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </label>
          </div>

          <label className="mt-4 flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground-800">反馈主题 *</span>
            <select
              name="subject"
              required
              className="rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 focus:border-primary-400"
            >
              {topicOptions.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>

          <label className="mt-4 flex flex-col gap-1.5">
            <span className="text-sm font-medium text-foreground-800">详细内容 *</span>
            <textarea
              name="message"
              required
              maxLength={500}
              rows={5}
              placeholder="请尽量说明事件背景，并附上可追溯的信源链接（如有）…"
              className="resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400"
            />
            <span className="text-xs text-foreground-400">最长 500 字</span>
          </label>

          {/* 反垃圾蜜罐字段（通过样式隐藏，不影响真人填写与无障碍） */}
          <div className="rdd-guard" aria-hidden="true">
            <label className="site-ref" htmlFor="website-verify">
              <input
                type="text"
                id="website-verify"
                name="contact_alt"
                className="site-ref"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </label>
          </div>

          {status === "error" && (
            <p className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-800">
              <i className="ri-error-warning-line mt-0.5" />
              {feedback}
            </p>
          )}
          {status === "success" && (
            <p className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line mt-0.5" />
              {feedback}
            </p>
          )}

          <button
            type="submit"
            disabled={status === "sending"}
            className="mt-6 inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
          >
            {status === "sending" ? (
              <>
                <i className="ri-loader-4-line mr-2 animate-spin" />
                提交中…
              </>
            ) : (
              <>
                提交反馈
                <i className="ri-send-plane-line ml-2" />
              </>
            )}
          </button>
        </form>
      </div>
    </SectionShell>
  );
}